import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
// Tombol close dengan style minimalis
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface Popup {
  id: string;
  title: string;
  content: string;
  is_active: boolean;
  button_text: string;
  button_url?: string;
  featured_image?: string;
  display_on: 'home' | 'blog' | 'both';
}

const WelcomePopup = () => {
  const [popup, setPopup] = useState<Popup | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const fetchActivePopup = async () => {
      try {
        // Cek apakah ini navigasi dari tombol 'Baca Artikel Lainnya'
        const fromArticleLink = sessionStorage.getItem('fromArticleLink');
        
        if (fromArticleLink) {
          console.log('Navigation from article link detected, skipping popup');
          sessionStorage.removeItem('fromArticleLink');
          return;
        }

        // Dapatkan path saat ini
        const currentPath = window.location.pathname;
        const isBlogPage = currentPath.startsWith('/blog');
        console.log('Current path:', currentPath, 'Is blog page:', isBlogPage);
        
        let query = supabase
          .from('popups')
          .select('*')
          .eq('is_active', true);
          
        // Filter berdasarkan halaman yang sesuai
        if (isBlogPage) {
          query = query.or('display_on.eq.blog,display_on.eq.both');
        } else {
          // Untuk halaman home
          query = query.or('display_on.eq.home,display_on.eq.both');
        }
        
        const { data, error } = await query
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (error) {
          console.log('No active popup found for this page:', error);
          return;
        }

        if (data) {
          // Pastikan data memiliki display_on dengan nilai default 'home' jika tidak ada
          const popupData: Popup = {
            id: data.id,
            title: data.title,
            content: data.content,
            is_active: data.is_active,
            button_text: data.button_text,
            button_url: data.button_url,
            featured_image: data.featured_image,
            display_on: (data as any).display_on || 'home' // Type assertion untuk handle field yang mungkin belum ada
          };
          setPopup(popupData);
          // Tampilkan popup hanya jika ada yang aktif untuk halaman ini
          setIsOpen(true);
        }
      } catch (error) {
        console.error('Error fetching popup:', error);
      }
    };

    fetchActivePopup();
  }, []);

  const handleClose = () => {
    if (popup) {
      localStorage.setItem(`popup_seen_${popup.id}`, new Date().toDateString());
    }
    setIsOpen(false);
  };

  const handleButtonClick = () => {
    if (popup?.button_url) {
      if (popup.button_url.startsWith('#')) {
        // Scroll to anchor
        const element = document.querySelector(popup.button_url);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        // Navigate to URL
        window.open(popup.button_url, '_blank');
      }
    }
    handleClose();
  };

  if (!popup || !isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-lg bg-background/95 backdrop-blur-sm border shadow-lg p-0 overflow-hidden">
        <div className="flex flex-col md:flex-row">
          {/* Bagian Gambar */}
          {popup.featured_image && (
            <div className="w-full md:w-1/2 h-48 md:h-auto">
              <img
                src={popup.featured_image}
                alt={popup.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Fallback ke div kosong jika gambar gagal dimuat
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            </div>
          )}
          
          {/* Bagian Konten */}
          <div className={`p-6 ${popup.featured_image ? 'md:w-1/2' : 'w-full'}`}>
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-foreground">
                {popup.title}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div 
                className="text-foreground/80 leading-relaxed prose prose-sm dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: popup.content }}
              />
              
              <div className="flex flex-col sm:flex-row-reverse gap-3 pt-2">
                <Button 
                  variant="outline"
                  onClick={handleClose}
                  className="w-full sm:w-auto order-2 sm:order-1"
                >
                  Tutup
                </Button>
                <Button 
                  onClick={handleButtonClick}
                  className="w-full sm:flex-1 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white order-1 sm:order-2"
                >
                  {popup.button_text}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WelcomePopup;